import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect';
import { useEffect, useRef, PropsWithChildren } from 'react';
import { create } from 'zustand';

const useSetting = create(set => ({
  setting: null,
  setSetting: (setting: any) => set({ setting }),
}));

function update() {
  document.documentElement.classList.add('changing-theme');
  const el = document.querySelector('meta[name="theme-color"]');

  if (
    (document && localStorage.theme === 'dark') ||
    (!('theme' in localStorage) &&
      window.matchMedia('(prefers-color-scheme: dark)').matches)
  ) {
    document.documentElement.classList.add('dark');

    if (!el) {
      //console.log("fail");
    } else {
      el.setAttribute('content', '#0B1120');
    }
  } else {
    document.documentElement.classList.remove('dark');
    if (!el) {
      //console.log("fail2");
    } else {
      el.setAttribute('content', '#f8fafc');
    }
    // document
    // 	.querySelector('meta[name="theme-color"]')
    // 	.setAttribute("content", "#f8fafc");
  }
  window.setTimeout(() => {
    document.documentElement.classList.remove('changing-theme');
  });
}

type IconProps = {
  className?: string;
  label?: string;
};

function SunIcon({ ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}>
      <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
      <path d="M12 4v1M17.66 6.344l-.828.828M20.005 12.004h-1M17.66 17.664l-.828-.828M12 20.01V19M6.34 17.664l.835-.836M3.995 12.004h1.01M6 6l.835.836" />
    </svg>
  );
}

function MoonIcon({ ...props }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.715 15.15A6.5 6.5 0 0 1 9 6.035C6.106 6.922 4 9.645 4 12.867c0 3.94 3.153 7.136 7.042 7.136 3.101 0 5.734-2.032 6.673-4.853Z"
        className="fill-transparent stroke-gray-500"
      />
      <path
        className="dark:fill-ivory fill-gray-500"
        d="m17.715 15.15.95.316a1 1 0 0 0-1.445-1.185l.495.869ZM9 6.035l.846.534a1 1 0 0 0-1.14-1.49L9 6.035Zm8.221 8.246a5.47 5.47 0 0 1-2.72.718v2a7.47 7.47 0 0 0 3.71-.98l-.99-1.738Zm-2.72.718A5.5 5.5 0 0 1 9 9.5H7a7.5 7.5 0 0 0 7.5 7.5v-2ZM9 9.5c0-1.079.31-2.082.845-2.93L8.153 5.5A7.47 7.47 0 0 0 7 9.5h2Zm-4 3.368C5 10.089 6.815 7.75 9.292 6.99L8.706 5.08C5.397 6.094 3 9.201 3 12.867h2Zm6.042 6.136C7.718 19.003 5 16.268 5 12.867H3c0 4.48 3.588 8.136 8.042 8.136v-2Zm5.725-4.17c-.81 2.433-3.074 4.17-5.725 4.17v2c3.552 0 6.553-2.327 7.622-5.537l-1.897-.632Z"
      />
      <path
        className="dark:fill-ivory fill-gray-500"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17 3a1 1 0 0 1 1 1 2 2 0 0 0 2 2 1 1 0 1 1 0 2 2 2 0 0 0-2 2 1 1 0 1 1-2 0 2 2 0 0 0-2-2 1 1 0 1 1 0-2 2 2 0 0 0 2-2 1 1 0 0 1 1-1Z"
      />
    </svg>
  );
}

function useTheme() {
  let { setting, setSetting }: any = useSetting();
  let initial = useRef(true);

  useIsomorphicLayoutEffect(() => {
    let theme = localStorage.theme;
    if (theme === 'light' || theme === 'dark') {
      setSetting(theme);
    } else {
      setSetting('system');
    }
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (setting === 'system') {
      localStorage.removeItem('theme');
    } else if (setting === 'light' || setting === 'dark') {
      localStorage.theme = setting;
    }
    if (initial.current) {
      initial.current = false;
    } else {
      update();
    }
  }, [setting]);

  useEffect(() => {
    let mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener('change', update);
    } else {
      mediaQuery.addListener(update);
    }

    function onStorage() {
      update();
      let theme = localStorage.theme;
      if (theme === 'light' || theme === 'dark') {
        setSetting(theme);
      } else {
        setSetting('system');
      }
    }
    window.addEventListener('storage', onStorage);

    return () => {
      if (mediaQuery?.removeEventListener) {
        mediaQuery.removeEventListener('change', update);
      } else {
        mediaQuery.removeListener(update);
      }

      window.removeEventListener('storage', onStorage);
    };
  }, [setSetting]);

  return [setting, setSetting];
}

type Props = {
  className?: string;
  showText?: boolean;
};

export const ThemeToggle: React.FC<PropsWithChildren<Props>> = ({
  className = '',
  showText = false,
  children,
}) => {
  let [setting, setSetting] = useTheme();

  const onClickToggle = (setting: string) => {
    return setting === 'light' ? setSetting('dark') : setSetting('light');
  };

  return (
    <button
      onClick={() => onClickToggle(setting)}
      className={`flex cursor-pointer items-center ${className}`}>
      <>
        <svg
          className="size-9 fill-current transition-all hover:scale-110"
          viewBox="0 0 50 50"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.6237 0.191341C14.4874 1.16071 8.1782 5.1398 4.09894 11.2435C-1.36631 19.4214 -1.36631 30.5599 4.09894 38.7379C8.84835 45.8444 16.5569 50 24.9898 50C37.5603 50 48.1521 40.6918 49.7875 28.2079C51.5963 14.3991 41.6538 1.70336 27.7892 0.117486C26.1899 -0.0653951 23.2546 -0.0302255 21.6237 0.191341ZM29.4398 3.22633C38.2888 5.14657 44.8533 11.7468 46.7545 20.6355C47.0251 21.9002 47.0587 22.3958 47.0543 25.0558C47.0501 27.6576 47.0112 28.2274 46.759 29.3867C44.8269 38.2657 38.2662 44.8293 29.3841 46.7698C28.2135 47.0255 27.6738 47.0613 24.9898 47.0611C22.2983 47.0611 21.7733 47.0261 20.6281 46.7702C11.6007 44.7521 5.13601 38.2795 3.22515 29.3458C2.95459 28.0812 2.921 27.5855 2.92543 24.9256C2.92959 22.3307 2.96892 21.7524 3.21851 20.6057C5.12546 11.8469 11.6503 5.21457 20.2374 3.30643C21.9721 2.921 22.7234 2.86356 25.4456 2.90811C27.6098 2.94354 28.4424 3.00997 29.4398 3.22633ZM21.632 6.17482C19.5842 6.54905 17.1003 7.43506 15.485 8.3673L14.8991 8.70545L19.2935 8.70701L23.6878 8.70858V7.34088V5.97318L23.1345 5.98725C22.83 5.99507 22.154 6.07947 21.632 6.17482ZM26.4221 25.0627V44.164L27.2358 44.0734C29.1731 43.8577 30.7499 43.4218 32.9322 42.498C36.4935 40.9905 40.1806 37.4511 42.0103 33.783C43.2198 31.3584 43.7735 29.4228 44.0022 26.8205C44.0868 25.8569 44.117 24.5967 44.0694 24.02C44.0217 23.4435 43.9614 22.5321 43.9354 21.9948C43.8316 19.851 42.4673 16.4335 40.7668 14.0572C39.8134 12.725 38.4052 11.0532 38.2364 11.0532C38.1688 11.0532 37.8119 10.7801 37.4433 10.4462C35.2057 8.41915 31.0418 6.54996 27.9194 6.17104C27.5613 6.12754 27.078 6.06254 26.8452 6.02672L26.4221 5.96133V25.0627ZM10.5635 12.5439C10.1319 13.0055 9.59493 13.6503 9.37046 13.9767L8.96241 14.5701H16.3251H23.6878V13.1373V11.7045H17.5182H11.3484L10.5635 12.5439ZM7.17577 18.2499C6.89375 19.005 6.50105 20.2887 6.50105 20.4554C6.50105 20.514 10.3681 20.5619 15.0944 20.5619H23.6878V19.064V17.566H15.5595H7.43122L7.17577 18.2499ZM6.03649 23.5993C6.00029 23.6936 5.9874 24.3824 6.00798 25.1298L6.04535 26.4886L14.8666 26.5221L23.6878 26.5556V24.9916V23.4276H14.8951C7.74032 23.4276 6.09 23.4595 6.03649 23.5993ZM6.50105 29.526C6.50105 29.6927 6.89375 30.9764 7.17577 31.7315L7.43122 32.4153H15.5595H23.6878V30.9174V29.4194H15.0944C10.3681 29.4194 6.50105 29.4673 6.50105 29.526ZM9.49038 36.1701C9.78308 36.5874 10.3187 37.2615 10.681 37.6681L11.3394 38.4071H17.5136H23.6878V36.9092V35.4112H16.323H8.95824L9.49038 36.1701ZM15.3548 41.5547C17.311 42.7603 21.293 44.0082 23.1841 44.0082H23.6878V42.6405V41.2728L19.2935 41.2733L14.8991 41.2737L15.3548 41.5547Z"
          />
        </svg>
        {showText && (
          <div className="ml-2">
            {setting === 'dark' ? 'Light mode' : 'Dark mode'}
          </div>
        )}
      </>
    </button>
  );
};
